import type { PresenceRequest, PresenceResponse } from "@serendiptv/contracts";

const HEARTBEAT_MS = 20_000;
const STALLED_MS = 10_000;
const COUNT_MAX_AGE_MS = 60_000;

type PresenceOptions = {
  video: HTMLVideoElement;
  visibility: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;
  lifecycle: Pick<Window, "addEventListener" | "removeEventListener">;
  sessionId: string;
  viewerId: string;
  sequence: { current: number };
  enabled: boolean;
  send: (input: PresenceRequest, signal?: AbortSignal, keepalive?: boolean) => Promise<PresenceResponse>;
  publish: (count: number | null) => void;
  publishStation?: (count: number | null, channel: number | null) => void;
};

/** Presence observes playback; it never starts, stops, seeks, or selects media. */
export class PresenceMonitor {
  private enabled: boolean;
  private channel: number | null = null;
  private externalPlayback: boolean | null = null;
  private playbackAllowed = true;
  private disposed = false;
  private departed = false;
  private lastMediaTime: number;
  private lastProgress = -Infinity;
  private lastSentAt = -Infinity;
  private lastWatching: boolean | null = null;
  private lastCountAt = -Infinity;
  private count: number | null = null;
  private timer?: ReturnType<typeof setInterval>;
  private pending?: AbortController;
  private timeout?: ReturnType<typeof setTimeout>;
  private readonly mediaEvents = ["timeupdate", "playing", "pause", "ended", "error", "emptied", "waiting", "seeking"];

  constructor(private readonly options: PresenceOptions) {
    this.enabled = options.enabled;
    this.lastMediaTime = options.video.currentTime;
  }

  start(): void {
    for (const event of this.mediaEvents) this.options.video.addEventListener(event, this.tick);
    this.options.visibility.addEventListener("visibilitychange", this.visibilityChanged);
    this.options.lifecycle.addEventListener("pagehide", this.pageHidden);
    this.options.lifecycle.addEventListener("pageshow", this.pageShown);
    this.timer = setInterval(this.tick, 1_000);
    this.tick();
  }

  setEnabled(enabled: boolean, playbackAllowed = true, channel: number | null = null, externalPlayback: boolean | null = null): void {
    if (this.enabled === enabled && this.playbackAllowed === playbackAllowed && this.channel === channel && this.externalPlayback === externalPlayback) return;
    if (this.channel !== channel) {
      this.channel = channel;
      this.options.publishStation?.(null, channel);
      this.lastWatching = null;
    }
    this.externalPlayback = externalPlayback;
    this.enabled = enabled;
    this.playbackAllowed = playbackAllowed;
    this.lastProgress = -Infinity;
    this.lastMediaTime = this.options.video.currentTime;
    this.tick();
  }

  stop(): void {
    this.disposed = true;
    clearInterval(this.timer);
    for (const event of this.mediaEvents) this.options.video.removeEventListener(event, this.tick);
    this.options.visibility.removeEventListener("visibilitychange", this.visibilityChanged);
    this.options.lifecycle.removeEventListener("pagehide", this.pageHidden);
    this.options.lifecycle.removeEventListener("pageshow", this.pageShown);
    this.release();
  }

  private visibilityChanged = () => {
    this.lastProgress = -Infinity;
    this.lastMediaTime = this.options.video.currentTime;
    this.tick();
  };

  private pageHidden = () => {
    this.departed = true;
    this.lastProgress = -Infinity;
    this.release();
  };

  private pageShown = () => {
    this.departed = false;
    this.lastMediaTime = this.options.video.currentTime;
    this.tick();
  };

  private release(): void {
    this.pending?.abort();
    clearTimeout(this.timeout);
    if (this.lastWatching === null) return;
    this.lastWatching = false;
    void this.options.send(this.payload(false), undefined, true).catch(() => undefined);
  }

  private payload(watching: boolean): PresenceRequest {
    return {
      session_id: this.options.sessionId,
      viewer_id: this.options.viewerId,
      sequence: ++this.options.sequence.current,
      watching,
      channel: this.channel
    };
  }

  private tick = () => {
    if (this.disposed || this.departed) return;
    const time = Date.now();
    const video = this.options.video;
    const foreground = this.enabled && this.options.visibility.visibilityState === "visible";
    const playable = foreground && this.playbackAllowed && (this.externalPlayback !== null ? this.externalPlayback : !video.paused && !video.ended && !video.error && !video.seeking && video.readyState >= 2);
    if (playable && (this.externalPlayback === true || video.currentTime > this.lastMediaTime)) this.lastProgress = time;
    this.lastMediaTime = video.currentTime;
    if (!playable) this.lastProgress = -Infinity;
    const watching = playable && time - this.lastProgress < STALLED_MS;
    if (time - this.lastCountAt > COUNT_MAX_AGE_MS && this.count !== null) {
      this.count = null;
      this.options.publish(null);
      this.options.publishStation?.(null, this.channel);
    }
    if (watching !== this.lastWatching || (foreground && time - this.lastSentAt >= HEARTBEAT_MS)) {
      this.lastWatching = watching;
      this.lastSentAt = time;
      this.refresh(watching);
    }
  };

  private refresh(watching: boolean): void {
    this.pending?.abort();
    clearTimeout(this.timeout);
    const controller = new AbortController();
    this.pending = controller;
    const request = this.payload(watching);
    this.timeout = setTimeout(() => controller.abort(), 8_000);
    void this.options.send(request, controller.signal, this.options.visibility.visibilityState !== "visible").then((response) => {
      if (this.disposed || controller.signal.aborted || request.sequence !== this.options.sequence.current) return;
      this.lastCountAt = Date.now();
      this.count = response.watching_now;
      this.options.publish(this.count);
      this.options.publishStation?.(response.channel === this.channel ? response.station_watching_now ?? null : null, this.channel);
    }).catch(() => {
      // Retain the last successful count only until its freshness limit.
    }).finally(() => {
      if (this.pending === controller) clearTimeout(this.timeout);
    });
  }
}
